import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { invariant } from 'ts-invariant';

import { TrackViewItem } from '@boc/analyticsV3';
import {
  StructuredData,
  convertSaleorToProductGroupSchema,
  createProductGroupSchema,
} from '@boc/framework';

import type { Route } from '../routes/+types/details';

import { absoluteify, parseParams, urls } from '~/urls';

import {
  ProductBasicInfoProvider,
  ProductDetailsProvider,
} from '~/framework/routes/products/providers';

import { Breadcrumbs } from '~/local/components/breadcrumbs';
import { getProductBreadcrumbs } from '~/local/products/breadcrumbs';

import { brandInfo } from '#/brand';

import type { IReviewStats } from '~/lib/reviews.types';
import { AddToCartOrWishlist, DiscountBadge, Price, SubTitle, Title } from './components';
import ProductReviews from './components/reviews';
import { KeyAttributes, ShortDescription } from './components/description';
import { Gallery } from './components/images';
import { ProductTag } from './components/product-tag';
import { ProductRecommendations } from './components/recommendations';
import { StockStatus } from './components/stock-status';
import { VariantSelector } from './variant-selector';

export function ProductPage({ params, loaderData }: Route.ComponentProps) {
  const { product, relatedProductsGroups } = loaderData;
  const { channel } = parseParams(params);

  const productSchema =
    product.variants && product.variants.length > 0
      ? createProductGroupSchema(
          convertSaleorToProductGroupSchema({
            product,
            variants: product.variants,
            channel,
            siteUrl: absoluteify('/'),
            baseProductUrl: absoluteify(urls.products.details(params, { slug: product.slug })),
            organization: brandInfo,
          })
        )
      : null;

  const [searchParams, setSearchParams] = useSearchParams();

  // Use URL query params as the single source of truth
  const variantId =
    searchParams.get('variant') ||
    product.defaultVariant?.id ||
    (product.variants && product.variants[0].id) ||
    null;

  invariant(product, "Couldn't find product");
  invariant(product.variants?.length, "couldn't find product variant");

  const variant = product.variants?.find((v) => v.id === variantId) || product.variants?.[0];

  invariant(variant, "Couldn't find variant");

  const setVariantId = (variantId: string | null) => {
    if (variantId) {
      setSearchParams((prev) => {
        prev.set('variant', variantId);
        return prev;
      });
    }
  };

  const reviewStats = useMemo<IReviewStats | null>(() => {
    try {
      const meta = product.metadata?.find((m: { key: string; value: string }) => m.key === 'reviews');
      if (!meta?.value) return null;
      const parsed = JSON.parse(meta.value);
      return parsed.totalCount > 0 ? parsed : null;
    } catch {
      return null;
    }
  }, [product.metadata]);

  const breadcrumbs = getProductBreadcrumbs(product, params);

  return (
    <>
      {productSchema && <StructuredData schema={productSchema} />}
      <TrackViewItem
        variant={{ ...variant, product: product }}
        itemUrl={(v) => absoluteify(urls.products.details(params, { slug: v.product.slug }))}
      />
      <ProductDetailsProvider
        product={product}
        category={product.category}
        variant={variant}
        setVariant={setVariantId}
        relatedProducts={relatedProductsGroups}
      >
        <ProductBasicInfoProvider product={product} variant={variant} setVariant={setVariantId}>
          <main className="text-primary-2-80 h-full w-full">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <div className="my-6 flex w-full flex-col gap-10 lg:h-[650px] lg:flex-row lg:gap-20">
              <div className="h-full md:basis-7/12">
                <Gallery />
              </div>
              <div className="scrollbar-hide flex h-full flex-col gap-y-6 md:basis-5/12 lg:overflow-y-auto">
                <div className="flex items-center space-x-3">
                  <StockStatus />
                  <DiscountBadge />
                </div>
                <div className="space-y-1">
                  <Title />
                  <SubTitle />
                </div>
                <VariantSelector />
                <div className="space-y-4">
                  <Price />
                  <AddToCartOrWishlist />
                </div>
                <div className="divide-primary-3-10 border-y-primary-3-10 mt-8 flex flex-col divide-y border-y text-base leading-5 tracking-tight *:py-8">
                  <ShortDescription />
                  <KeyAttributes />
                  <ProductTag product={product} ProductDetails />
                </div>
              </div>
            </div>
            {reviewStats && (
              <div className="my-10">
                <ProductReviews productId={product.id} stats={reviewStats} />
              </div>
            )}
            <div className="my-10">
              <ProductRecommendations />
            </div>
          </main>
        </ProductBasicInfoProvider>
      </ProductDetailsProvider>
    </>
  );
}
